import React from 'react';
import {gql, useMutation, useQuery} from '@apollo/client';
import {Box, Button, Divider, Grid} from '@mui/material';
import moment from 'moment';
import {useSnackbar} from 'notistack';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import {Panel} from '../../components/Panel/Panel';
import {NOTIFICATION_FRAGMENT} from '../../graphql/fragments';
import {AdminGetAllNotifications} from '../../schema-types/AdminGetAllNotifications';
import Loading from '../../components/Loading/Loading';
import {NotificationFragment} from '../../schema-types/NotificationFragment';
import AutoField, {ActionType, FormField} from '../../components/AutoForm/AutoFieldNew';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {
  UpsertNotification,
  UpsertNotificationVariables,
} from '../../schema-types/UpsertNotification';
import {
  DeleteNotification,
  DeleteNotificationVariables,
} from '../../schema-types/DeleteNotification';

type notificationsFormProps = {
  existingNotification: NotificationFragment | null;
};
type notificationsFormType = {
  message: string;
  expiresAfter: Date;
};

export const ManageGlobalNotificationsForm: React.FC<notificationsFormProps> = ({
  existingNotification,
}) => {
  const {submitForm} = useAutoForm();

  const [onDelete] = useMutation<DeleteNotification, DeleteNotificationVariables>(gql`
    mutation DeleteNotification($notificationId: UUID!) {
      deleteNotification(notificationId: $notificationId) {
        ok
        errors
      }
    }
  `);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <AutoField
          field={{
            type: ActionType.text,
            fullWidth: true,
            minRows: 1,
            label: 'Message',
            id: 'message',
          }}
        />
      </Grid>
      <Grid item>
        <AutoField
          field={{type: ActionType.datepicker, id: 'expiresAfter', label: 'Ends at'}}
        />
      </Grid>
      <Grid item>
        <Button variant="contained" color="primary" onClick={() => submitForm()}>
          Save
        </Button>
      </Grid>
      {existingNotification?.id && (
        <Grid item>
          <Button
            variant="contained"
            color="secondary"
            onClick={() =>
              onDelete({
                variables: {
                  notificationId: existingNotification.id,
                },
              }).then(() => {
                window.location.reload();
              })
            }
          >
            Delete
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

const ManageGlobalNotificationsWrapped = withAutoFormNew<
  notificationsFormType,
  notificationsFormProps,
  FormField
>(ManageGlobalNotificationsForm, {}, {});

export const ManageGlobalNotifications = () => {
  const [onSubmit, {loading}] = useMutation<
    UpsertNotification,
    UpsertNotificationVariables
  >(gql`
    mutation UpsertNotification(
      $notificationId: UUID
      $message: String!
      $expiresAfter: DateTime!
    ) {
      upsertNotification(
        notificationId: $notificationId
        message: $message
        expiresAfter: $expiresAfter
      ) {
        notification {
          ...NotificationFragment
        }
      }
    }
    ${NOTIFICATION_FRAGMENT}
  `);

  const {data} = useQuery<AdminGetAllNotifications>(gql`
    query AdminGetAllNotifications {
      notifications {
        ...NotificationFragment
      }
    }
    ${NOTIFICATION_FRAGMENT}
  `);
  const {enqueueSnackbar} = useSnackbar();

  if (!data?.notifications) {
    return <Loading entityName="notifications" />;
  }

  return (
    <>
      <Panel title="Manage existing notifications">
        {data.notifications.length ? (
          data.notifications.map((notification) => {
            return (
              <Box key={notification.id} p={2}>
                <ManageGlobalNotificationsWrapped
                  initialValueOverrides={{
                    message: notification.message || '',
                    expiresAfter: notification.expiresAfter,
                  }}
                  existingNotification={notification}
                  formFields={[]}
                  submit={async (values) => {
                    return onSubmit({
                      variables: {
                        message: values.message,
                        expiresAfter: moment.utc(values.expiresAfter || new Date()),
                        notificationId: notification.id,
                      },
                    }).then(() => {
                      window.location.reload();
                    });
                  }}
                />
                <Divider style={{marginTop: '2rem'}} />
              </Box>
            );
          })
        ) : (
          <Box p={2}>No current notifications</Box>
        )}
      </Panel>
      <Box mt={4} />
      <Panel title="Create notification">
        <Box p={2}>
          <ManageGlobalNotificationsWrapped
            existingNotification={null}
            formFields={[]}
            submit={async (values) => {
              if (!values.message) {
                return enqueueSnackbar('Missing message', {
                  variant: 'error',
                });
              }
              return onSubmit({
                variables: {
                  message: values.message,
                  expiresAfter: moment.utc(values.expiresAfter || new Date()),
                },
              }).then(() => {
                window.location.reload();
              });
            }}
          />
        </Box>
      </Panel>
    </>
  );
};
